import * as React from "react";
import { ReactNode } from "react";

import InfoIcon from "@mui/icons-material/Info";
import MuiTooltip from "@mui/material/Tooltip";
import { TooltipProps as MuiTooltipProps } from "@mui/material/Tooltip";

import styled from "vistar/ui/veneer/styled";

const MUI_DEFAULT_WIDTH = 300;

export type TooltipProps = Pick<
  MuiTooltipProps,
  "arrow" | "placement" | "title"
> & { children?: ReactNode; dataTestId?: string; width?: number };

export type ControlledTooltipProps = TooltipProps &
  Pick<
    MuiTooltipProps,
    "disableHoverListener" | "open" | "onMouseEnter" | "onMouseLeave"
  >;

export type InfoTooltipProps = Omit<TooltipProps, "children">;

const LightOpacityInfoIcon = styled(InfoIcon)({
  opacity: "70%",
});

// the `<span>` below is the officially documented workaround which enables
// `disabled` child components of `Tooltip` components to render a tooltip
// when disabled (the UX expected at Vistar)
// https://mui.com/material-ui/react-tooltip/#disabled-elements

const Tooltip = ({
  arrow = false,
  children,
  dataTestId,
  placement = "top",
  title,
  width = MUI_DEFAULT_WIDTH,
  ...rest
}: TooltipProps) => (
  <MuiTooltip
    data-component="veneer-tooltip"
    data-testid={dataTestId}
    arrow={arrow}
    title={title}
    placement={placement}
    componentsProps={{
      tooltip: {
        sx: {
          maxWidth: width,
        },
      },
    }}
    {...rest}
  >
    <span>{children}</span>
  </MuiTooltip>
);

export const ControlledTooltip = ({
  arrow = false,
  children,
  dataTestId,
  disableHoverListener,
  open,
  onMouseEnter,
  onMouseLeave,
  placement = "top",
  title,
  width = MUI_DEFAULT_WIDTH,
}: ControlledTooltipProps) => (
  <MuiTooltip
    arrow={arrow}
    componentsProps={{
      tooltip: {
        sx: {
          maxWidth: width,
        },
      },
    }}
    data-component="veneer-controlled-tooltip"
    data-testid={dataTestId}
    disableHoverListener={disableHoverListener}
    open={open}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    placement={placement}
    title={title}
  >
    <span>{children}</span>
  </MuiTooltip>
);

export const InfoTooltip = ({
  arrow = false,
  dataTestId,
  placement = "top",
  title,
  width,
}: InfoTooltipProps) => (
  <Tooltip
    arrow={arrow}
    dataTestId={dataTestId}
    title={title}
    placement={placement}
    width={width}
  >
    <LightOpacityInfoIcon fontSize="small" />
  </Tooltip>
);

export default Tooltip;
